"use client";

import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const clampPage = (page: number, totalPages: number): number => {
  const lastPage = Math.max(totalPages - 1, 0);
  return Math.min(Math.max(page, 0), lastPage);
};

export interface TablePaginationProps {
  /** Zero-based current page index. */
  page: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  /** Plural noun, e.g. "orders", "trades", "payments". Defaults to "items". */
  itemLabel?: string;
  onPageChange: (page: number) => void;
  rowsPerPageOptions?: number[];
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  /**
   * Force the compact (mobile-style) treatment regardless of viewport. When omitted, the
   * component renders the rich layout at `sm:` and up and the compact bare nav below.
   */
  compact?: boolean;
  className?: string;
}

const RichLayout = ({
  page,
  totalPages,
  totalItems,
  itemsPerPage,
  itemLabel,
  rowsPerPageOptions,
  onPageChange,
  onRowsPerPageChange,
}: Required<
  Pick<
    TablePaginationProps,
    "page" | "totalPages" | "totalItems" | "itemsPerPage" | "itemLabel" | "rowsPerPageOptions" | "onPageChange"
  >
> & {
  onRowsPerPageChange?: (rowsPerPage: number) => void;
}) => {
  const safePage = clampPage(page, totalPages);
  const startItem = totalItems === 0 ? 0 : safePage * itemsPerPage + 1;
  const endItem = Math.min((safePage + 1) * itemsPerPage, totalItems);

  return (
    <>
      <div className="text-[11px] text-cede-text-secondary">
        Showing {startItem}-{endItem} of {totalItems} {itemLabel}
      </div>

      <div className="flex items-center gap-3">
        {onRowsPerPageChange ? (
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-cede-text-secondary">Rows per page:</span>
            <Select value={itemsPerPage.toString()} onValueChange={(value) => onRowsPerPageChange(parseInt(value))}>
              <SelectTrigger className="w-16">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rowsPerPageOptions.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <Button
            onClick={() => onPageChange(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            variant="outline"
            size="small"
          >
            Previous
          </Button>
          <span className="px-2 text-[11px] text-cede-text-secondary">
            Page {safePage + 1} of {totalPages || 1}
          </span>
          <Button
            onClick={() => onPageChange(Math.min(Math.max(totalPages - 1, 0), safePage + 1))}
            disabled={safePage >= totalPages - 1}
            variant="outline"
            size="small"
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
};

const CompactLayout = ({
  page,
  totalPages,
  onPageChange,
}: Pick<TablePaginationProps, "page" | "totalPages" | "onPageChange">) => {
  const safePage = clampPage(page, totalPages);
  const isFirstPage = safePage === 0;
  const isLastPage = safePage >= totalPages - 1;

  return (
    <>
      <Button
        onClick={() => onPageChange(Math.max(0, safePage - 1))}
        disabled={isFirstPage}
        variant="ghost"
        className="h-6 w-6 p-0"
        aria-label="Previous page"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </Button>
      <span className="font-mono tabular-nums text-[11px] text-cede-text-secondary">
        {safePage + 1}/{totalPages || 1}
      </span>
      <Button
        onClick={() => onPageChange(Math.min(Math.max(totalPages - 1, 0), safePage + 1))}
        disabled={isLastPage}
        variant="ghost"
        className="h-6 w-6 p-0"
        aria-label="Next page"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </Button>
    </>
  );
};

export const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  totalPages,
  totalItems,
  itemsPerPage,
  itemLabel = "items",
  onPageChange,
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  onRowsPerPageChange,
  compact,
  className,
}) => {
  if (compact) {
    return (
      <div
        className={cn("flex items-center justify-end gap-1 border-t border-cede-border-container px-2 py-1", className)}
      >
        <CompactLayout page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
    );
  }

  return (
    <div className={cn("border-t border-cede-border-container", className)}>
      {/* Rich layout (sm and up) */}
      <div className="hidden items-center justify-between px-3 py-2 sm:flex">
        <RichLayout
          page={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          itemLabel={itemLabel}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>

      {/* Compact layout (below sm) */}
      <div className="flex items-center justify-end gap-1 px-2 py-1 sm:hidden">
        <CompactLayout page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
    </div>
  );
};
